import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { PanelHeader } from './_shared';
import { openConfirm } from '@/components/admin/ConfirmDialog';

const PROJECT_ROLES = ['editor', 'contributor', 'ordinary'];

export default function MembershipsPanel() {
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState(null);
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [draft, setDraft] = useState({ user_id: '', role: 'contributor' });

  const reload = async () => {
    try {
      const [u] = await Promise.all([adminApi.users()]);
      setUsers(u.users || []);
      const list = await adminApi.browse('projects', { limit: 200 });
      setProjects(list.items || []);
    } catch { toast.error('Could not load data'); }
  };
  useEffect(() => { reload(); }, []);

  const openProject = async (pid) => {
    setActive(pid);
    try {
      const r = await adminApi.projectMembers(pid);
      setMembers(r.members || []);
    } catch { toast.error('Could not load members'); }
  };

  const addMember = async () => {
    if (!active || !draft.user_id) return toast.error('Pick a user');
    try {
      await adminApi.addProjectMember(active, draft.user_id, draft.role);
      toast.success('Member added');
      setDraft({ user_id: '', role: 'contributor' });
      openProject(active);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
  };

  const removeMember = async (uid) => {
    if (!(await openConfirm({
      title: `Remove ${uid.slice(-12)} from project?`,
      tone: 'destructive',
    }))) return;
    try {
      await adminApi.removeProjectMember(active, uid);
      toast.success('Removed');
      openProject(active);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Remove failed'); }
  };

  return (
    <section>
      <PanelHeader title="Project Memberships" subtitle="Assign users to projects with editor / contributor / ordinary roles. The project owner is always an editor." onRefresh={reload} />

      <div className="mt-4 grid gap-3 lg:grid-cols-3">
        <div className="rounded-md border border-border bg-card">
          <div className="border-b border-border bg-panel-header/40 px-3 py-2 text-[10.5px] uppercase tracking-wider text-muted-foreground flex items-center justify-between">
            <span>Projects</span>
            <button onClick={reload} className="hover:text-foreground"><RefreshCcw className="h-3 w-3" /></button>
          </div>
          <ul className="max-h-[480px] overflow-auto scroll-thin">
            {projects.map((p) => (
              <li key={p.id}>
                <button onClick={() => openProject(p.id)} className={`flex w-full items-start gap-2 border-b border-border px-3 py-2 text-left text-[12.5px] hover:bg-secondary/60 ${active === p.id ? 'bg-primary/10' : ''}`} data-testid={`admin-memb-proj-${p.id}`}>
                  <div className="min-w-0">
                    <div className="truncate font-medium">{p.name}</div>
                    <div className="font-mono-stat text-[10.5px] text-muted-foreground truncate">{p.id}</div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-2 rounded-md border border-border bg-card">
          {!active ? (
            <div className="p-6 text-[12px] text-muted-foreground">Select a project on the left.</div>
          ) : (
            <>
              <div className="border-b border-border bg-panel-header/40 px-3 py-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                Members of <span className="font-mono-stat normal-case text-foreground">{active}</span>
              </div>
              <div className="divide-y divide-border">
                {members.length === 0 && <div className="px-3 py-4 text-[12px] text-muted-foreground">No members yet.</div>}
                {members.map((m) => (
                  <div key={m.user_id} className="grid grid-cols-12 items-center gap-2 px-3 py-2 text-[12.5px]" data-testid={`admin-memb-row-${m.user_id}`}>
                    <div className="col-span-4 min-w-0">
                      <div className="truncate font-medium">{m.name || m.email || m.user_id}</div>
                      <div className="font-mono-stat text-[10.5px] text-muted-foreground truncate">{m.user_id}</div>
                    </div>
                    <div className="col-span-4 truncate text-[11.5px] text-muted-foreground">{m.email}</div>
                    <div className="col-span-3 capitalize text-[11.5px]">
                      {m.role}{m.is_owner && <span className="ml-1.5 rounded-sm bg-primary/15 px-1 text-[9.5px] uppercase tracking-wider text-primary">owner</span>}
                    </div>
                    <div className="col-span-1 text-right">
                      {!m.is_owner && (
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" onClick={() => removeMember(m.user_id)} data-testid={`admin-memb-remove-${m.user_id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-border bg-panel-header/30 px-3 py-3">
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <Label className="text-[11px]">User</Label>
                    <select
                      value={draft.user_id}
                      onChange={(e) => setDraft({ ...draft, user_id: e.target.value })}
                      className="h-8 w-64 rounded-sm border border-border bg-background px-2 text-[12px]"
                      data-testid="admin-memb-user-select"
                    >
                      <option value="">— Select user —</option>
                      {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email || u.user_id.slice(-12)}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[11px]">Role</Label>
                    <select
                      value={draft.role}
                      onChange={(e) => setDraft({ ...draft, role: e.target.value })}
                      className="h-8 w-32 rounded-sm border border-border bg-background px-2 text-[12px] capitalize"
                      data-testid="admin-memb-role-select"
                    >
                      {PROJECT_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <Button size="sm" className="h-8 gap-1.5" onClick={addMember} data-testid="admin-memb-add"><Plus className="h-3.5 w-3.5" /> Add member</Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
